/**
 * Design System - Effects Tokens
 * Defines border-radius, shadows, and other visual effects
 */

export const borderRadius = {
  // Round values
  none: '0',
  xs: '2px',
  sm: '4px',
  base: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  xl2: '20px',
  xl3: '24px',
  full: '9999px',

  // Aliases for semantic usage
  small: '4px',
  medium: '8px',
  large: '12px',
  extraLarge: '16px',
};

/**
 * Shadow tokens for elevation and depth
 * Two-layer shadows for sophisticated depth perception
 * Inner layer: smaller, darker (local shadow)
 * Outer layer: larger, lighter (ambient shadow)
 */
export const shadows = {
  // No shadow
  none: 'none',

  // Elevation 1 - Subtle
  sm: '0 2px 3px hsla(0,0%,0%,0.07), 0 3px 8px hsla(0,0%,0%,0.05)',

  // Elevation 2 - Light
  base: '0 2px 4px hsla(0,0%,0%,0.1), 0 4px 10px hsla(0,0%,0%,0.06)',

  // Elevation 3 - Medium
  md: '0 4px 6px hsla(0,0%,0%,0.12), 0 6px 15px hsla(0,0%,0%,0.08)',

  // Elevation 4 - Prominent
  lg: '0 6px 12px hsla(0,0%,0%,0.15), 0 10px 20px hsla(0,0%,0%,0.1)',

  // Elevation 5 - Strong
  xl: '0 8px 16px hsla(0,0%,0%,0.18), 0 12px 28px hsla(0,0%,0%,0.12)',

  // Elevation 6 - Heavy
  xl2: '0 12px 24px hsla(0,0%,0%,0.2), 0 16px 40px hsla(0,0%,0%,0.15)',

  // Elevation 7 - Very Heavy
  xl3: '0 16px 32px hsla(0,0%,0%,0.22), 0 20px 50px hsla(0,0%,0%,0.18)',

  // Inset shadow (for pressed/sunken effect)
  inset: 'inset 0 2px 4px hsla(0,0%,0%,0.15), inset 0 -1px 2px hsla(0,0%,100%,0.5)',

  // Focus shadow (for focus states)
  focus: '0 0 0 3px hsla(59,96%,47%,0.2), 0 0 0 5px hsla(59,96%,47%,0.1)',
};

/**
 * Shadow tokens for dark themes
 * Two-layer shadows optimized for dark mode
 * Stronger to maintain visibility against dark backgrounds
 */
export const shadowsDark = {
  none: 'none',
  sm: '0 2px 3px hsla(0,0%,0%,0.35), 0 3px 8px hsla(0,0%,0%,0.25)',
  base: '0 2px 4px hsla(0,0%,0%,0.4), 0 4px 10px hsla(0,0%,0%,0.3)',
  md: '0 4px 6px hsla(0,0%,0%,0.45), 0 6px 15px hsla(0,0%,0%,0.35)',
  lg: '0 6px 12px hsla(0,0%,0%,0.5), 0 10px 20px hsla(0,0%,0%,0.4)',
  xl: '0 8px 16px hsla(0,0%,0%,0.55), 0 12px 28px hsla(0,0%,0%,0.45)',
  xl2: '0 12px 24px hsla(0,0%,0%,0.6), 0 16px 40px hsla(0,0%,0%,0.5)',
  xl3: '0 16px 32px hsla(0,0%,0%,0.65), 0 20px 50px hsla(0,0%,0%,0.55)',
  inset: 'inset 0 2px 4px hsla(0,0%,0%,0.5), inset 0 -1px 2px hsla(0,0%,100%,0.1)',
  focus: '0 0 0 3px hsla(59,96%,47%,0.3), 0 0 0 5px hsla(59,96%,47%,0.15)',
};

/**
 * Border tokens
 */
export const borders = {
  // Border widths
  width: {
    none: '0',
    thin: '1px',
    base: '2px',
    thick: '3px',
  },

  // Border styles
  style: {
    solid: 'solid',
    dashed: 'dashed',
    dotted: 'dotted',
  },

  // Common border presets
  default: '1px solid',
  thick: '2px solid',
  divider: '1px solid',
};

/**
 * Transition/Animation tokens
 */
export const transitions = {
  // Durations
  duration: {
    instant: '0ms',
    fast: '150ms',
    base: '200ms',
    slow: '300ms',
    slower: '500ms',
  },

  // Timing functions
  easing: {
    linear: 'linear',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Presets
  default: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  fast: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
};

/**
 * Opacity tokens for state variations
 */
export const opacity = {
  disabled: 0.5,
  hover: 0.8,
  focus: 0.9,
  active: 1,
};
